import React, { useState, useEffect, useCallback } from "react";

import { getSocket } from "../utils/socket";


const checkChatLimit = async (role, token, fetchWithRetry) => {
  try {
    const response = await fetchWithRetry({
      method: "get",
      url: "/api/chat/role/user-chat-restrictions",
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data || {
      maxMessages: 0,
      videoSupport: false,
      priority: false,
      virtualGifts: false,
      enabled: true,
      hideMessages: true,
      hideMessagesWithUser: false,
    };
  } catch (error) {
    console.error("Lỗi khi lấy cấu hình giới hạn chat:", error);
    return {
      maxMessages: 0,
      videoSupport: false,
      priority: false,
      virtualGifts: false,
      enabled: true,
      hideMessages: true,
      hideMessagesWithUser: false,
    };
  }
};

const getFormattedDate = () => {
  const days = ["Chủ Nhật", "Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy"];
  const date = new Date();
  const dayName = days[date.getDay()];
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${dayName}, ${day} tháng ${month}, ${year}`;
};

const ChatRestriction = ({
  userId,
  token,
  messages,
  onSendMessage,
  authUser,
  targetUser,
  setMessages,
  newMessage,
  setChatLimit,
  fetchWithRetry,
}) => {
  const [chatLimit, setLocalChatLimit] = useState({
    maxMessages: 0,
    videoSupport: false,
    priority: false,
    virtualGifts: false,
    enabled: true,
    hideMessages: true,
    hideMessagesWithUser: false,
  });
  const [error, setError] = useState(null);

  const fetchChatLimit = useCallback(async () => {
    try {
      const config = await checkChatLimit(authUser?.role || "user", token, fetchWithRetry);
      setLocalChatLimit(config);
      if (setChatLimit) setChatLimit(config);
      setError(null);
    } catch (err) {
      console.error("Không thể lấy cấu hình giới hạn chat:", err);
      setError("Không thể tải cấu hình chat. Vui lòng thử lại!");
    }
  }, [authUser?.role, token, fetchWithRetry, setChatLimit]);

  useEffect(() => {
    fetchChatLimit();

    const socket = getSocket();
    if (!socket) {
      console.warn("Socket chưa sẵn sàng, bỏ qua lắng nghe thông báo giới hạn chat");
      return;
    }

    socket.on("notification", (notification) => {
      if (
        notification.type === "chat_restriction_updated" &&
        notification.role === authUser?.role
      ) {
        fetchChatLimit();
      }
      if (notification.type === "chat_restrictions_toggled") {
        setLocalChatLimit((prev) => ({ ...prev, enabled: notification.enabled }));
        if (setChatLimit) setChatLimit((prev) => ({ ...prev, enabled: notification.enabled }));
      }
    });

    return () => {
      socket.off("notification");
    };
  }, [authUser?.role, fetchChatLimit, setChatLimit]);

  const handleSendMessage = useCallback(() => {
    if (!chatLimit.enabled) {
      return onSendMessage();
    }

    const sentMessagesCount = messages.filter(
      (msg) => msg.senderId === authUser._id && !msg.isSystemMessage
    ).length;

    if (chatLimit.maxMessages > 0 && sentMessagesCount >= chatLimit.maxMessages) {
      const notificationMessage = {
        _id: Date.now().toString(),
        senderId: "system",
        receiverId: authUser._id,
        content: `Bạn đã đạt giới hạn ${chatLimit.maxMessages} tin nhắn hôm nay. Vui lòng nâng cấp vai trò của bạn để gửi thêm!`,
        createdAt: new Date(),
        isTemp: true,
        isSystemMessage: true,
        date: getFormattedDate(),
      };
      setMessages((prev) => {
        const hasSimilarMessage = prev.some(
          (msg) =>
            msg.isSystemMessage &&
            msg.content === notificationMessage.content &&
            Math.abs(new Date(msg.createdAt) - new Date()) < 60000
        );
        if (hasSimilarMessage) return prev;
        return [...prev, notificationMessage];
      });
      return false;
    }

    return onSendMessage();
  }, [chatLimit, messages, authUser._id, onSendMessage, setMessages]);

  return (
    <div className="relative flex items-center">
      {error && (
        <div className="absolute -top-12 left-0 bg-red-100 text-red-700 px-3 py-1 rounded-lg text-sm">
          {error}
        </div>
      )}
      <button
        onClick={handleSendMessage}
        className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-r-xl hover:from-blue-600 hover:to-blue-700 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-400"
        disabled={!newMessage.trim()}
      >
        Gửi
      </button>
    </div>
  );
};

export { getFormattedDate };
export default ChatRestriction;